import React, { useState, useEffect, useMemo } from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { VideoCard } from "../../molecules/video-card";
import { Pagination } from "../../atoms/Pagination";
import LoadingIndicatorComponent from "../../atoms/loading-indicator";
import { getCollectionitems } from "../../../../api/utils";
import { filterStoriesByForum } from "../../../utils/forum-filter";
import { useModalEffect } from "../../../hooks/useModalEffect";
import { RESEARCH_HUB_CONTENT } from "../../../constants";
import "./video-archive-template.m.css";

const VideoArchiveTemplate = ({ collection, pageName = "Video Archive", themeClass = "", shouldApplyForumFilter = false }) => {
    const member = useSelector((state) => get(state, ["member"], null));
    const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
    const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));

    const [videoModal, setVideoModal] = useState({ isOpen: false, embedUrl: '' });
    const [vimeoThumbnails, setVimeoThumbnails] = useState({});
    const [openTextCard, setOpenTextCard] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [additionalItems, setAdditionalItems] = useState([]);

    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        try {
            const parsedUrl = new URL(url);
            if (parsedUrl.hostname.includes("youtu.be")) return parsedUrl.pathname.slice(1);
            if (parsedUrl.searchParams.get("v")) return parsedUrl.searchParams.get("v");
            if (parsedUrl.pathname.includes("/embed/")) return parsedUrl.pathname.split("/embed/")[1];
            return null;
        } catch {
            return null;
        }
    };

    const fetchVimeoThumbnail = async (vimeoUrl, videoId) => {
        try {
            const response = await fetch(`https://vimeo.com/api/oembed.json?url=${vimeoUrl}`);
            const data = await response.json();
            if (data?.thumbnail_url) {
                setVimeoThumbnails(prev => ({ ...prev, [videoId]: data.thumbnail_url }));
            }
        } catch (error) {
            console.error(`[${pageName}] Error fetching Vimeo thumbnail:`, error);
        }
    };

    const initialItems = useMemo(() => {
        const items = collection.items || [];
        return items.reduce((acc, item) => {
            if (item && item.type === 'collection' && Array.isArray(item.items)) {
                return [...acc, ...item.items];
            }
            return item ? [...acc, item] : acc;
        }, []);
    }, [collection.items]);

    useEffect(() => {
        const totalCount = collection['total-count'] || 0;
        const currentCount = initialItems.length;
        const slug = collection.slug;

        if (totalCount > currentCount && slug) {
            const fetchRemaining = async () => {
                try {
                    const remaining = await getCollectionitems(slug, currentCount, totalCount - currentCount);
                    if (remaining && remaining.length > 0) {
                        setAdditionalItems(remaining);
                    }
                } catch (error) {
                    console.error(`[${pageName}] Error fetching additional items:`, error);
                }
            };
            fetchRemaining();
        }
    }, [collection.slug, collection['total-count'], initialItems.length]);

    const allItems = useMemo(() => {
        if (additionalItems.length === 0) return initialItems;
        const existingIds = new Set(initialItems.map(item => item.id || item?.story?.id));
        const newItems = additionalItems.filter(item => {
            const id = item.id || item?.story?.id;
            return !existingIds.has(id);
        });
        return [...initialItems, ...newItems];
    }, [initialItems, additionalItems]);


    const isVimeoContent = (element) => {
        if (!element) return false;
        if (element.subtype === 'vimeo-video') return true;
        const embedUrl = element['embed-url'] || '';
        const url = element.url || element.metadata?.['vimeo-url'] || '';
        return embedUrl.includes('vimeo.com') || url.includes('vimeo.com');
    };

    const extractVimeoId = (element) => {
        if (!element) return null;
        if (element.metadata?.['video-id']) return String(element.metadata['video-id']);
        const text = element['embed-url'] || element.url || element.metadata?.['vimeo-url'] || '';
        const match = text.match(/vimeo\.com\/(?:video\/)?(\d+)|player\.vimeo\.com\/video\/(\d+)/);
        return match ? (match[1] || match[2]) : null;
    };

    const getStoryDates = (metadataWhen, publishedAt) => {
        const pAt = Number(publishedAt) || 0;
        let pTime = pAt;
        let eYear = null;

        if (metadataWhen) {
            const d = new Date(metadataWhen);
            if (!isNaN(d.getTime())) {
                pTime = d.getTime();
                eYear = d.getFullYear();
            }
        }

        if (!eYear && pAt) {
            const d = new Date(pAt);
            if (!isNaN(d.getTime())) {
                eYear = d.getFullYear();
            }
        }

        return {
            primaryTime: pTime,
            secondaryTime: pAt,
            year: eYear
        };
    };

    const processedStories = useMemo(() => {
        try {
            const itemsToProcess = shouldApplyForumFilter 
                ? filterStoriesByForum(allItems, member, subscriberMetadata) 
                : allItems;

            const result = itemsToProcess
                .filter(item => {
                    const story = item?.story || item;
                    return story?.['story-template'] === 'video';
                })
                .map(item => {
                    const story = item?.story || item;
                    const { cards = [], metadata = {} } = story || {};
                    const [firstElement, secondElement] = cards[0]?.['story-elements'] || [];

                    const isVimeo = isVimeoContent(firstElement);
                    const rawEmbed = firstElement?.['embed-url'] || '';
                    const videoUrl = firstElement?.url || firstElement?.metadata?.['vimeo-url'];

                    const vimeoId = isVimeo ? extractVimeoId(firstElement) : null;
                    const videoId = isVimeo ? vimeoId : getYouTubeVideoId(videoUrl);

                    const videoEmbed = isVimeo
                        ? (vimeoId ? `https://player.vimeo.com/video/${vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479` : rawEmbed)
                        : (rawEmbed || '');

                    const thumbnail = isVimeo
                        ? (vimeoThumbnails[videoId] || '')
                        : (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '');

                    const vimeoUrlForFetch = isVimeo
                        ? (videoUrl || (vimeoId ? `https://vimeo.com/${vimeoId}` : null))
                        : null;

                    const videoDesc =
                        secondElement?.text ||
                        secondElement?.description ||
                        cards[1]?.['story-elements']?.[0]?.text ||
                        firstElement?.metadata?.description ||
                        '';

                    const { primaryTime, secondaryTime, year } = getStoryDates(metadata?.when, story?.['published-at']);

                    return {
                        id: item.id || story?.id || Math.random().toString(36).substr(2, 9),
                        type: 'video',
                        category: '',
                        title: story?.headline || '',
                        date: '',
                        publishedAt: story?.['published-at'],
                        updatedAt: story?.['updated-at'],
                        metadataWhen: metadata?.when,
                        speakerName: metadata?.expert || '',
                        speakerPosition: metadata?.['expert-description'] || '',
                        videoEmbed,
                        videoDesc,
                        videoThumbnail: thumbnail,
                        videoId,
                        vimeoUrl: vimeoUrlForFetch,
                        primaryTime,
                        secondaryTime,
                        effectiveYear: year
                    };
                });
            return result;
        } catch (error) {
            console.error(`[${pageName}] Error processing stories:`, error);
            return [];
        }
    }, [allItems, vimeoThumbnails, pageName, member, subscriberMetadata, shouldApplyForumFilter]);

    useEffect(() => {
        processedStories.forEach(story => {
            if (story.vimeoUrl && story.videoId && !vimeoThumbnails[story.videoId]) {
                fetchVimeoThumbnail(story.vimeoUrl, story.videoId);
            }
        });
    }, [processedStories]);

    const years = useMemo(() => {
        const yearSet = new Set();
        processedStories.forEach(story => {
            if (story.effectiveYear) {
                yearSet.add(story.effectiveYear);
            }
        });
        return Array.from(yearSet).sort((a, b) => b - a);
    }, [processedStories]);

    useEffect(() => {
        if (!selectedYear && years.length > 0) {
            setSelectedYear(years[0]);
        }
    }, [years, selectedYear]);

    const filteredStories = useMemo(() => {
        if (!selectedYear) return processedStories;
        return processedStories.filter(story => story.effectiveYear === selectedYear);
    }, [processedStories, selectedYear]);

    const sortedFilteredStories = useMemo(() => {
        const result = [...filteredStories].sort((a, b) => {
            const diff = (b.primaryTime || 0) - (a.primaryTime || 0);
            if (diff !== 0) return diff;
            return (b.secondaryTime || 0) - (a.secondaryTime || 0);
        });
        return result;
    }, [filteredStories]);

    const latestStories = useMemo(() => {
        const result = [...processedStories]
            .sort((a, b) => {
                const diff = (b.primaryTime || 0) - (a.primaryTime || 0);
                if (diff !== 0) return diff;
                return (b.secondaryTime || 0) - (a.secondaryTime || 0);
            })
            .slice(0, 2);
        return result;
    }, [processedStories]);

    const ITEMS_PER_PAGE = 8;

    const gridStories = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return sortedFilteredStories.slice(startIndex, endIndex);
    }, [sortedFilteredStories, currentPage]);

    const totalPages = Math.ceil(sortedFilteredStories.length / ITEMS_PER_PAGE);

    const handlePlayVideo = (videoEmbed) => {
        setVideoModal({ isOpen: true, embedUrl: videoEmbed });
    };

    useModalEffect(videoModal.isOpen);

    const getEmbedSrc = (embedUrl) => {
        if (!embedUrl) return '';
        if (embedUrl.includes('<iframe')) {
            const match = embedUrl.match(/src="([^"]+)"/);
            return match ? match[1] : '';
        }
        return embedUrl;
    };

    const handleCardToggle = (cardId) => {
        setOpenTextCard(openTextCard === cardId ? null : cardId);
    };

    const handleYearChange = (year) => {
        setSelectedYear(year);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const VideoModal = ({ isOpen, embedUrl, onClose }) => {
        if (!isOpen) return null;
        const src = getEmbedSrc(embedUrl);
        return (
            <div styleName="modal-overlay" onClick={onClose}>
                <div styleName="modal-content" onClick={(e) => e.stopPropagation()}>
                    <button styleName="close-button" onClick={onClose}>×</button>
                    <div styleName="video-container">
                        <iframe
                            src={src}
                            title="Video Player"
                            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            styleName="video-iframe"
                        ></iframe>
                    </div>
                </div>
            </div>
        );
    };

    const hasItems = collection && collection.items && collection.items.length > 0;
    const isInitialYearLoading = hasItems && years.length > 0 && !selectedYear;

    if (isAuthInitializing || (member && !subscriberMetadata) || isInitialYearLoading) {
        return <LoadingIndicatorComponent />;
    }

    if (!hasItems) {
        return (
            <div styleName={`video-archive-template ${themeClass}`}>
                <div styleName="container" style={{ alignItems: 'center' }}>
                    <h2 styleName="empty_state_message">No stories found</h2>
                </div>
            </div>
        );
    }

    return (
        <div styleName={`video-archive-template ${themeClass}`}>
            <div styleName="container">
                <div styleName="header-wrapper">
                    <h1 styleName="page-title">{collection.name || ''}</h1>
                    {(collection.summary || collection.description) && (
                        <div
                            styleName="page-description"
                            dangerouslySetInnerHTML={{ __html: collection.summary || collection.description }}
                        />
                    )}
                </div>

                {latestStories.length > 0 && (
                    <section styleName="latest-section">
                        <h2 styleName="section-title">Latest</h2>
                        <div styleName="latest-grid">
                            {latestStories.map(story => {
                                if (!story.videoEmbed) return null;
                                const cardId = `latest-${story.id}`;
                                return (
                                    <VideoCard
                                        key={cardId}
                                        item={story}
                                        isTextView={openTextCard === cardId}
                                        onToggle={() => handleCardToggle(cardId)}
                                        onPlayVideo={handlePlayVideo}
                                    />
                                );
                            })}
                        </div>
                    </section>
                )}

                <nav styleName="tabs-section">
                    {years.map(year => (
                        <div
                            key={year}
                            styleName={`tab ${selectedYear === year ? 'active' : ''}`}
                            onClick={() => handleYearChange(year)}
                        >
                            <span styleName="tab-text">{year}</span>
                        </div>
                    ))}
                </nav>

                <section styleName="video-grid">
                    {gridStories.map(story => {
                        if (!story.videoEmbed) return null;
                        const cardId = `grid-${story.id}`;
                        return (
                            <VideoCard
                                key={cardId}
                                item={story}
                                isTextView={openTextCard === cardId}
                                onToggle={() => handleCardToggle(cardId)}
                                onPlayVideo={handlePlayVideo}
                            />
                        );
                    })}
                </section>

                <div styleName="pagination-container">
                    <Pagination
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>

            <VideoModal
                isOpen={videoModal.isOpen}
                embedUrl={videoModal.embedUrl}
                onClose={() => setVideoModal({ isOpen: false, embedUrl: '' })}
            />
        </div>
    );
};

VideoArchiveTemplate.propTypes = {
    collection: PropTypes.object.isRequired,
    pageName: PropTypes.string,
    themeClass: PropTypes.string,
    shouldApplyForumFilter: PropTypes.bool
};

export { VideoArchiveTemplate };
